import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BACKEND_URL } from '../../Constants';
import { v4 as uuid } from 'uuid';

const BACKEND = `${BACKEND_URL}/api/chatbotai-quizzes`;

const ChatbotAIQuizzesEditor = () => {
  const { id } = useParams();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Draft');
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(''); // For temporary user feedback
  const [showImportModal, setShowImportModal] = useState(false); // State for import modal visibility
  const [importJsonText, setImportJsonText] = useState(''); // State for JSON input in modal

  // Function to show a temporary message
  const showTemporaryMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000); // Clear message after 3 seconds
  };

  // ✅ Load quiz data on mount
  useEffect(() => {
    if (!id) return;
    fetch(`${BACKEND}/${id}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status} - ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        setTitle(data.title || '');
        setDescription(data.description || '');
        setStatus(data.status || 'Draft');
        setQuestions(data.questions || []);
      })
      .catch((err) => console.error('Failed to fetch quiz', err));
  }, [id]);

  const addEmptyQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        id: uuid(), // Client-side unique ID for new questions
        text: '',
        choices: { a: '', b: '', c: '', d: '' },
        correct: 'a',
        plainAnswer: ''
      }
    ]);
  };

  const deleteQuestion = (qid) => {
    // Check if the question has a MongoDB _id (meaning it's already saved on the backend)
    // MongoDB _id's are typically 24-character hexadecimal strings.
    // If it's an existing question, add its _id to the list of questions to be deleted from the backend.
    if (typeof qid === 'string' && qid.length === 24 && /^[0-9a-fA-F]{24}$/.test(qid)) {
      setDeletedQuestionIds((prev) => [...prev, qid]);
    }
    // Filter out the question from the local state to update the UI immediately.
    // This handles both new questions (with uuid) and existing questions (with _id).
    setQuestions((prev) =>
      prev.filter((q) => (q._id || q.id) !== qid)
    );
    showTemporaryMessage('Question marked for deletion.');
  };

  const handleSave = async () => {
    if (!title.trim()) {
      console.log('Please enter a quiz title.');
      showTemporaryMessage('Please enter a quiz title.');
      return;
    }
    setSaving(true);
    showTemporaryMessage('Saving quiz...');

    try {
      // ✅ Update quiz metadata
      const quizUpdateRes = await fetch(`${BACKEND}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, status })
      });
      if (!quizUpdateRes.ok) {
        const errorText = await quizUpdateRes.text();
        throw new Error(`Failed to update quiz metadata: Status ${quizUpdateRes.status}, Body: ${errorText}`);
      }

      // ✅ Delete removed questions from the backend
      const failedDeletions = [];
      for (const qid of deletedQuestionIds) {
        try {
          console.log(`Attempting to delete question with ID: ${qid}`);
          const deleteRes = await fetch(`${BACKEND}/${id}/question/${qid}`, {
            method: 'DELETE'
          });
          if (!deleteRes.ok) {
            const errorText = await deleteRes.text();
            throw new Error(`Status: ${deleteRes.status}, Body: ${errorText}`);
          }
          console.log(`Successfully sent delete request for question ID: ${qid}`);
        } catch (error) {
          console.error(`Error deleting question ${qid}:`, error);
          failedDeletions.push(qid);
        }
      }
      if (failedDeletions.length > 0) {
        console.log(`⚠️ Failed to delete the following questions on the backend: ${failedDeletions.join(', ')}. Please check your backend server logs.`);
        showTemporaryMessage(`Failed to delete some questions. Check console.`);
      }


      // ✅ Save new or updated questions
      for (const q of questions) {
        // If this question was an existing one and also marked for deletion, skip it.
        if (q._id && deletedQuestionIds.includes(q._id)) {
          continue;
        }

        const payload = {
          text: q.text,
          choices: q.choices,
          correct: q.correct,
          plainAnswer: q.plainAnswer
        };

        try {
          if (q._id) { // If question has a MongoDB _id, it's an existing question to update
            console.log(`Attempting to update question with ID: ${q._id}`);
            const updateQuestionRes = await fetch(`${BACKEND}/${id}/question/${q._id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            if (!updateQuestionRes.ok) {
              const errorText = await updateQuestionRes.text();
              throw new Error(`Failed to update question ${q._id}: Status: ${updateQuestionRes.status}, Body: ${errorText}`);
            }
            console.log(`Successfully updated question ID: ${q._id}`);
          } else { // If no _id, it's a new question to create
            console.log(`Attempting to add new question with client ID: ${q.id}`);
            const addQuestionRes = await fetch(`${BACKEND}/${id}/question`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            if (!addQuestionRes.ok) {
              const errorText = await addQuestionRes.text();
              throw new Error(`Failed to add new question: Status: ${addQuestionRes.status}, Body: ${errorText}`);
            }
            console.log(`Successfully added new question (client ID: ${q.id})`);
          }
        } catch (error) {
          console.error(`Error saving/updating question ${q._id || q.id}:`, error);
          showTemporaryMessage(`Error saving a question. Check console.`);
        }
      }

      // ✅ Refresh quiz from backend to get updated _ids for newly added questions
      const freshRes = await fetch(`${BACKEND}/${id}`);
      if (!freshRes.ok) {
        const errorText = await freshRes.text();
        throw new Error(`Failed to refresh quiz after save: Status ${freshRes.status}, Body: ${errorText}`);
      }
      const fresh = await freshRes.json();
      setQuestions(fresh.questions || []);
      setDeletedQuestionIds([]); // Clear deleted IDs after successful save and refresh
      console.log('✅ Quiz saved successfully.');
      showTemporaryMessage('✅ Quiz saved successfully!');
    } catch (err) {
      console.error('An error occurred during save:', err);
      console.log('⚠️ Failed to save quiz. Check console for details.');
      showTemporaryMessage('⚠️ Failed to save quiz. Check console.');
    } finally {
      setSaving(false);
    }
  };

  const handleImportQuestions = () => {
    try {
      const parsedQuestions = JSON.parse(importJsonText);
      if (!Array.isArray(parsedQuestions)) {
        throw new Error('Imported data is not an array of questions.');
      }

      const newQuestions = parsedQuestions.map(q => ({
        id: uuid(), // Assign new client-side ID
        text: q.text || '',
        choices: q.choices || { a: '', b: '', c: '', d: '' },
        correct: q.correct || 'a',
        plainAnswer: q.plainAnswer || ''
      }));

      setQuestions(prev => [...prev, ...newQuestions]);
      setShowImportModal(false);
      setImportJsonText(''); // Clear the textarea
      showTemporaryMessage(`Successfully imported ${newQuestions.length} questions.`);
    } catch (error) {
      console.error('Error importing questions:', error);
      showTemporaryMessage(`Error importing questions: ${error.message}`);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">Edit Quiz</h2>

      {/* Temporary message display */}
      {message && (
        <div className="alert alert-info text-center" role="alert">
          {message}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <h4 className="mb-3">Quiz Details</h4>

          <div className="mb-3">
            <label className="form-label">Title:</label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter quiz title"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Description:</label>
            <textarea
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Status:</label>
            <select
              className="form-control"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="Draft">Draft</option>
              <option value="Published">Published</option>
            </select>
          </div>
        </div>
      </div>

      {questions.map((q, index) => (
        <div key={q._id || q.id} className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <h4>Question {index + 1}</h4>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => deleteQuestion(q._id || q.id)}
              >
                Delete
              </button>
            </div>

            <div className="mb-2">
              <label className="form-label">Text:</label>
              <input
                className="form-control"
                type="text"
                value={q.text}
                onChange={(e) => {
                  const updated = [...questions];
                  updated[index].text = e.target.value;
                  setQuestions(updated);
                }}
              />
            </div>

            {['a', 'b', 'c', 'd'].map((key, optIndex) => (
              <div key={key} className="mb-2">
                <label className="form-label">Option {optIndex + 1}:</label>
                <input
                  className="form-control"
                  type="text"
                  value={q.choices[key]}
                  onChange={(e) => {
                    const updated = [...questions];
                    updated[index].choices[key] = e.target.value;
                    setQuestions(updated);
                  }}
                />
              </div>
            ))}

            <div className="mb-2">
              <label className="form-label">Correct Answer:</label>
              <select
                className="form-control"
                value={q.correct}
                onChange={(e) => {
                  const updated = [...questions];
                  updated[index].correct = e.target.value;
                  setQuestions(updated);
                }}
              >
                <option value="a">Option 1 (A)</option>
                <option value="b">Option 2 (B)</option>
                <option value="c">Option 3 (C)</option>
                <option value="d">Option 4 (D)</option>
              </select>
            </div>

            <div className="mb-2">
              <label className="form-label">Plain Explanation:</label>
              <textarea
                className="form-control"
                value={q.plainAnswer}
                onChange={(e) => {
                  const updated = [...questions];
                  updated[index].plainAnswer = e.target.value;
                  setQuestions(updated);
                }}
              />
            </div>
          </div>
        </div>
      ))}

      <button className="btn btn-primary me-3" onClick={addEmptyQuestion}>
        + Add Question
      </button>
      <button className="btn btn-info me-3" onClick={() => setShowImportModal(true)} disabled={saving}>
        Import Questions
      </button>
      <button className="btn btn-success" onClick={handleSave} disabled={saving}>
        💾 {saving ? 'Saving...' : 'Save Quiz'}
      </button>

      {/* Import Questions Modal */}
      {showImportModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Questions (JSON Array)</h5>
                <button type="button" className="close" onClick={() => setShowImportModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Paste an array of question objects in JSON format below:</p>
                <textarea
                  className="form-control"
                  rows="10"
                  value={importJsonText}
                  onChange={(e) => setImportJsonText(e.target.value)}
                  placeholder={`[
  {
    "text": "What is the capital of France?",
    "choices": { "a": "Berlin", "b": "Paris", "c": "Rome", "d": "Madrid" },
    "correct": "b",
    "plainAnswer": "Paris is the capital and most populous city of France."
  },
  {
    "text": "Which planet is known as the Red Planet?",
    "choices": { "a": "Earth", "b": "Mars", "c": "Jupiter", "d": "Venus" },
    "correct": "b",
    "plainAnswer": "Mars is often referred to as the Red Planet."
  }
]`
                  }
                ></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleImportQuestions}>
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotAIQuizzesEditor;
